import React, { useState } from "react"
import { User } from "lucide-react"
import { needsImageProxy, proxiedImageUrl } from "../utils"

/**
 * Small circular profile/creator avatar. Self-hides (renders nothing) if
 * there's no URL or it fails to load, rather than leaving a broken-image
 * glyph in a tiny circle.
 *
 * `placeholder` opts into a generic filled circle instead of self-hiding -
 * for a layout where every row/card must line up the same way whether or not
 * a photo loaded (InfluencerCard's grid, where TikTok's `profile_image_url`
 * is sometimes missing outright, or a dead signed URL). Rows without this
 * app's own mirrored copy still carry the platform's raw signed URL, so
 * `needsImageProxy`/`proxiedImageUrl` (utils.js) route anything `.heic`/
 * `.heif` through wsrv.nl to get it decoded and re-served as a JPEG a
 * browser can actually render.
 */
const Avatar = ({ src, alt, size = 18, style, placeholder = false }) => {
  const resolved = needsImageProxy(src) ? proxiedImageUrl(src) : src
  const [failed, setFailed] = useState(!resolved)

  if (failed) {
    if (!placeholder) return null
    return (
      <span
        title={alt || undefined}
        style={{
          width: size, height: size, borderRadius: "50%", flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "rgba(255,255,255,0.08)", color: "#6b7280",
          ...style,
        }}
      >
        <User size={Math.round(size * 0.55)} />
      </span>
    )
  }

  return (
    <img
      src={resolved}
      alt={alt || ""}
      onError={() => setFailed(true)}
      style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", flexShrink: 0, ...style }}
    />
  )
}

export default Avatar
